import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { AddForm } from '../crew/CrewTab';
import { CREW_KEY, type Assignment } from '../crew/types';
import { EVENTS_KEY, type EventView } from '../events/labels';
import { trapTab } from '../ui/focus';
import { DialogLayer } from '../ui/Dialog';
import { QUOTES_KEY } from './types';

/**
 * One suggestion of a crew rule (server: `crewSuggestions`): the rule wants `quantity` people of the function, `open`
 * places are free — both absent for whoever does not see the quote lines' „Ilość” (rule 7a).
 */
export type CrewSuggestion = { ruleId: string; ruleName: string; message: string; functionId: string; functionName: string; quantity?: number; open?: number };
export type RulePanelData = { suggestions: CrewSuggestion[]; canAccept: boolean };

/** „Dodaj: Realizator FOH” (one place) / „Dodaj: 2 × Technik” (more). */
export function suggestionTitle(s: Pick<CrewSuggestion, 'functionName' | 'open'>): string {
  return (s.open ?? 0) > 1 ? `${s.open} × ${s.functionName}` : s.functionName;
}

/** What the rules see changes with the draft's equipment: the panel asks again when a line or its quantity changes. */
export function linesSignature(lines: ReadonlyArray<{ id: string; quantity?: number | undefined }>): string {
  return lines.map((l) => `${l.id}:${l.quantity ?? ''}`).join(',');
}

/**
 * Rules in the quote (owner 2026-10-24): the panel „Reguły” of a draft, like the packlist's — what the crew rules
 * („Miejsce w obsadzie”) want and nobody fills yet. „Dodaj” adds a person of the function to Obsada (the quote's
 * service is priced in the quote already); „Pomiń” skips the rule for this event at once — no reason asked (owner
 * 2026-10-24 10:44) — here and in the packlist.
 * Both only for whoever may add people to the crew; the others see the note alone. Nothing to show: no panel.
 */
export function QuoteRulePanel({ eventId, versionId, signature }: { eventId: string; versionId: string; signature: string }) {
  const queryClient = useQueryClient();
  const key = [...QUOTES_KEY, 'rule-suggestions', versionId, signature];
  const data = useQuery({ queryKey: key, queryFn: () => api<RulePanelData>(`/api/quotes/versions/${versionId}/rule-suggestions`) });
  const [adding, setAdding] = useState<CrewSuggestion | null>(null);
  // the crew → quote and rules syncs run in the background: the quote's lines follow a moment later
  const refreshSoon = (keepPanel = false) => {
    // after „Pomiń” the panel already holds the server's fresh answer: only the rest of the quote is read again
    const again = () => void queryClient.invalidateQueries({ queryKey: QUOTES_KEY, predicate: (q) => !keepPanel || q.queryKey[1] !== 'rule-suggestions' });
    again();
    for (const ms of [1500, 4000]) setTimeout(again, ms);
  };
  const skip = useMutation({
    mutationFn: (s: CrewSuggestion) => api<RulePanelData>(`/api/quotes/versions/${versionId}/rule-skips`, { method: 'POST', body: { ruleId: s.ruleId } }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(key, fresh);
      refreshSoon(true);
    },
  });
  const list = data.data?.suggestions ?? [];
  if (list.length === 0) return null;
  const canAccept = data.data?.canAccept === true;
  return (
    <section className="rule-panel q-rule-panel" aria-label="Reguły" data-feature-id="quotes.rule_crew">
      <h3>Reguły</h3>
      {skip.error && (
        <p className="alert" role="alert">
          {skip.error instanceof ApiError ? skip.error.message : 'Nie udało się pominąć reguły'}
        </p>
      )}
      <ul className="list">
        {list.map((s) => (
          <li key={s.ruleId} className="row">
            <div className="row-main">
              <span className="chip">sugestia</span> <strong>{suggestionTitle(s)}</strong> <span className="muted">do obsady</span>
              <div className="muted">
                {s.ruleName}
                {s.message ? ` — ${s.message}` : ''}
              </div>
            </div>
            {canAccept && (
              <div className="row-actions">
                <button className="btn small" type="button" aria-label={`Dodaj: ${s.functionName}`} onClick={() => setAdding(s)}>
                  Dodaj
                </button>
                <button className="btn ghost small" type="button" aria-label={`Pomiń: ${s.ruleName}`} disabled={skip.isPending} onClick={() => skip.mutate(s)}>
                  Pomiń
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {adding && (
        <AddToCrewDialog
          eventId={eventId}
          suggestion={adding}
          onClose={(added) => {
            setAdding(null);
            if (added) refreshSoon();
          }}
        />
      )}
    </section>
  );
}

/** „Dodaj” of a suggestion: the crew's own „Dodaj osobę” with the rule's function chosen. */
function AddToCrewDialog({ eventId, suggestion, onClose }: { eventId: string; suggestion: CrewSuggestion; onClose: (added: boolean) => void }) {
  const event = useQuery({ queryKey: [...EVENTS_KEY, 'one', eventId], queryFn: () => api<EventView>(`/api/events/${eventId}`) });
  const crew = useQuery({ queryKey: [...CREW_KEY, 'event', eventId], queryFn: () => api<{ assignments: Assignment[] }>(`/api/crew/events/${eventId}/assignments`) });
  // the crew form's save runs inside: no closing (Esc, backdrop) while it is under way
  const busy = useIsMutating() > 0;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel q-rule-add"
        role="dialog"
        aria-modal="true"
        aria-label={`Dodaj do obsady: ${suggestion.functionName}`}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={() => onClose(false)} busy={busy} />
        {(event.isLoading || crew.isLoading) && <p className="muted" role="status">Wczytywanie…</p>}
        {(event.error || crew.error) && (
          <p className="alert" role="alert">
            Nie udało się wczytać obsady wydarzenia.
          </p>
        )}
        {event.data && crew.data && <AddForm event={event.data} taken={crew.data.assignments} initialFunctionId={suggestion.functionId} onDone={(added) => onClose(!!added)} />}
      </div>
    </div>
  );
}
